import React, { useContext, useState, useMemo } from 'react';
import { PlaytreeDataContext } from '../DataContexts/PlaytreeDataContext';
import { PlaylistDataContext } from '../DataContexts/PlaylistDataContext';
import CoverArtEditor from '../components/CoverArtEditor'
import { useNavigate } from 'react-router-dom';
import './PlaytreeManager.css';

const getPlaylistDisplayName = (playlist) =>
  playlist.name === 'YOUR FAVOURITES' ? (playlist.searchString || playlist.name) : playlist.name;

const byTypeThenName = (a, b) =>
  a.playlistType.localeCompare(b.playlistType) ||
  getPlaylistDisplayName(a).localeCompare(getPlaylistDisplayName(b));

const PlaytreeManager = ({ playtreeId, onOpenPlaylist }) => {
  const {
    playtrees,
    createPlaytree,
    addPlaylistToPlaytree,
    updatePlaytreeName,
    removePlaylistFromPlaytree,
    deletePlaytree,
    updateSublistOrder,
  } = useContext(PlaytreeDataContext);
  const { playlists } = useContext(PlaylistDataContext);
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [newPlaytreeName, setNewPlaytreeName] = useState('');
  const [selectedPlaytree, setSelectedPlaytree] = useState(null);
  const [selectedPlaylists, setSelectedPlaylists] = useState([]);
  const [playlistSearchQuery, setPlaylistSearchQuery] = useState('');
  const [playlistsMarkedForRemoval, setPlaylistsMarkedForRemoval] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [localSublistOrder, setLocalSublistOrder] = useState(null);

  const syncSelectedPlaytree = (playtreeList) => {
    if (selectedPlaytree) {
      const updated = playtreeList.find((pt) => pt.id === selectedPlaytree.id);
      if (updated) setSelectedPlaytree(updated);
    }
  };

  React.useEffect(() => {
    syncSelectedPlaytree(playtrees);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playtrees]);

  React.useEffect(() => {
    if (playtreeId && playtrees && playtrees.length > 0) {
      const found = playtrees.find(pt => pt.id === playtreeId);
      if (found) setSelectedPlaytree(found);
    }
  }, [playtreeId, playtrees]);

  const filteredPlaytrees = useMemo(() => {
    if (!playtrees) return [];
    return playtrees.filter((playtree) =>
      playtree.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [playtrees, searchQuery]);

  // The ONE id list that both the displayed rows and every mutation
  // (reorder, removal, save) operate on. Orphan ids — playlists that were
  // deleted but are still referenced by the playtree — are dropped here
  // once, so a displayed row index can never point at a different entry
  // than the mutation touches (DJB-73).
  const sublistIds = useMemo(() => {
    if (!selectedPlaytree || !playlists) return [];
    const order = localSublistOrder || selectedPlaytree.sublists;
    return order.filter(id => playlists.some(p => p.id === id));
  }, [selectedPlaytree, playlists, localSublistOrder]);

  const sublists = useMemo(
    () => sublistIds.map(id => playlists.find(p => p.id === id)),
    [sublistIds, playlists]
  );

  const filteredPlaylists = useMemo(() => {
    if (!playlists || !selectedPlaytree) return [];
    return playlists
      .filter(
        (playlist) =>
          !selectedPlaytree.sublists.includes(playlist.id) &&
          getPlaylistDisplayName(playlist).toLowerCase().includes(playlistSearchQuery.toLowerCase())
      )
      .sort(byTypeThenName);
  }, [playlists, selectedPlaytree, playlistSearchQuery]);

  const hasNameChanged = useMemo(() => {
    if (!selectedPlaytree || !isEditing) return false;
    return editName.trim() !== selectedPlaytree.name;
  }, [editName, selectedPlaytree, isEditing]);

  const hasOrderChanged = useMemo(() => {
    if (!localSublistOrder || !selectedPlaytree) return false;
    const serverOrder = selectedPlaytree.sublists;
    if (localSublistOrder.length !== serverOrder.length) return true;
    return localSublistOrder.some((id, i) => id !== serverOrder[i]);
  }, [localSublistOrder, selectedPlaytree]);

  const hasUnsavedChanges = hasNameChanged || hasOrderChanged || playlistsMarkedForRemoval.length > 0;

  const handleSelectPlaytree = (playtree) => {
    if (isEditing) {
      setIsEditing(false);
      setEditName('');
      setLocalSublistOrder(null);
      setPlaylistsMarkedForRemoval([]);
    }
    setSelectedPlaytree(playtree);
  };

  const handleAddToIntermediate = (playlist) => {
    if (!selectedPlaylists.find((selected) => selected.id === playlist.id)) {
      setSelectedPlaylists((prev) => [...prev, playlist]);
    }
  };

  const handleAddAllToIntermediate = () => {
    const newPlaylists = filteredPlaylists.filter(
      (playlist) => !selectedPlaylists.some((selected) => selected.id === playlist.id)
    );
    setSelectedPlaylists((prev) => [...prev, ...newPlaylists]);
  };

  const handleRemoveFromIntermediate = (playlistId) => {
    setSelectedPlaylists((prev) =>
      prev.filter((playlist) => playlist.id !== playlistId)
    );
  };

  const handleToggleRemoval = (playlistId) => {
    if (playlistsMarkedForRemoval.includes(playlistId)) {
      setPlaylistsMarkedForRemoval((prev) =>
        prev.filter((id) => id !== playlistId)
      );
    } else {
      setPlaylistsMarkedForRemoval((prev) => [...prev, playlistId]);
    }
  };

  const handleAddToPlaytree = async () => {
    if (selectedPlaytree && selectedPlaylists.length > 0) {
      try {
        for (const playlist of selectedPlaylists) {
          await addPlaylistToPlaytree(selectedPlaytree.id, playlist.id);
        }
        if (localSublistOrder) {
          setLocalSublistOrder(prev => [
            ...prev,
            ...selectedPlaylists.map(p => p.id),
          ]);
        }
        setSelectedPlaylists([]);
      } catch (error) {
        console.error('Failed to add playlists to playtree:', error);
      }
    }
  };

  // Reorder on sublistIds — the same array the rows are rendered from — so
  // the clicked row index always addresses the entry it displays (DJB-73)
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newOrder = [...sublistIds];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setLocalSublistOrder(newOrder);
  };

  const handleMoveDown = (index) => {
    if (index >= sublistIds.length - 1) return;
    const newOrder = [...sublistIds];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setLocalSublistOrder(newOrder);
  };

  const handleResetToAlphabetical = () => {
    if (!selectedPlaytree || !playlists) return;
    const sorted = [...sublistIds].sort((a, b) => {
      const pa = playlists.find(p => p.id === a);
      const pb = playlists.find(p => p.id === b);
      return getPlaylistDisplayName(pa).localeCompare(
        getPlaylistDisplayName(pb),
        undefined,
        { sensitivity: 'base' }
      );
    });
    setLocalSublistOrder(sorted);
  };

  const handleSave = async () => {
    if (!selectedPlaytree) return;
    if (hasNameChanged && !editName.trim()) return;

    const removals = [...playlistsMarkedForRemoval];

    try {
      if (hasNameChanged) {
        await updatePlaytreeName(selectedPlaytree.id, editName.trim());
      }

      if (removals.length > 0) {
        for (const playlistId of removals) {
          await removePlaylistFromPlaytree(selectedPlaytree.id, playlistId);
        }
        setPlaylistsMarkedForRemoval([]);
      }

      if (hasOrderChanged) {
        // localSublistOrder is built from sublistIds, so orphan ids are
        // already gone; saving a reorder also cleans them out of the
        // persisted order (DJB-73)
        const remainingOrder = localSublistOrder.filter(
          id => !removals.includes(id)
        );
        await updateSublistOrder(selectedPlaytree.id, remainingOrder);
      }

      setLocalSublistOrder(null);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditName('');
    setLocalSublistOrder(null);
    setPlaylistsMarkedForRemoval([]);
  };

  const handleCreatePlaytree = async () => {
    if (!newPlaytreeName.trim()) {
      alert('Please enter a name for the playtree.');
      return;
    }

    const newPlaytree = { name: newPlaytreeName.trim(), sublists: [] };
    try {
      await createPlaytree(newPlaytree);
      setNewPlaytreeName('');
    } catch (error) {
      console.error('Failed to create playtree:', error);
    }
  };

  const handleDeletePlaytree = async () => {
    if (selectedPlaytree) {
      const confirmDelete = window.confirm(
        `Are you sure you want to delete the category: "${selectedPlaytree.name}"?`
      );
      if (confirmDelete) {
        await deletePlaytree(selectedPlaytree.id);
        setSelectedPlaytree(null);
      }
    }
  };

  const handlePlaylistClick = (playlistId) => {
    navigate(`/playlists?playlistId=${playlistId}&playtreeId=${selectedPlaytree.id}`);
    // Inside the Curator page the URL alone is not enough: the parent expands
    // the Playlist Editor section and hands it the playlist to open (DJB-65)
    if (onOpenPlaylist) onOpenPlaylist(playlistId);
  };

  return (
    <div className="playtree-manager">
      {/* Create Category – centered wrap at the top of the section */}
      <div className="pm-create-wrap">
        <div className="create-playtree-container">
          <span className="create-playlist-label">Create new category:</span>
          <label>
            Name:
            <input
              type="text"
              className="create-playtree-name"
              value={newPlaytreeName}
              onChange={(e) => setNewPlaytreeName(e.target.value)}
            />
          </label>
          <button className="create-playtree-button" onClick={handleCreatePlaytree}>
            Create Category
          </button>
        </div>
      </div>

      {/* The legacy `columns`/`column` classes stay for the standalone
          /playtreemanager route; the pm-* classes carry the modern grid
          inside the Curator page (DJB-70). The editing modifier lets the
          grid widen the details column while a category is being edited
          without anything clipping (DJB-72) */}
      <div className={`columns pm-columns${isEditing ? ' pm-columns--editing' : ''}`}>
        {/* Left column - search and category list, modelled on the Playlist
            Subscriptions left column (no type filter: categories are untyped) */}
        <div className="column pm-column pm-column--list">
          <div className="ps-column-head">
            <span className="ps-column-label">Categories</span>
            <span className="ps-column-count">{filteredPlaytrees.length}</span>
          </div>
          <div className="ps-filters">
            <input
              type="text"
              placeholder="Search playtrees..."
              className="ps-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <ul className="playtree-list-content">
            {filteredPlaytrees.map((playtree) => (
              <li
                key={playtree.id}
                className={selectedPlaytree?.id === playtree.id ? 'selected' : ''}
                onClick={() => handleSelectPlaytree(playtree)}
              >
                <span className="playtree-name">{playtree.name}</span>
                <span className="playtree-records">
                  {playtree.sublists?.length || 0} playlists
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="column pm-column pm-column--details">
          {selectedPlaytree ? (
            <div className="playtree-details">
              <div className="playtree-header">
                <div>
                  <CoverArtEditor resourceId={selectedPlaytree.id} extension="png" template="playtree" />
                </div>
                <div className="playtree-info">
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="edit-input"
                      />
                      <div className="edit-buttonsPlaytrees">
                        {hasUnsavedChanges && (
                          <span className="unsaved-indicator">Unsaved changes</span>
                        )}
                        <button
                          className="edit-buttonPlaytrees"
                          onClick={handleSave}
                          disabled={!hasUnsavedChanges}
                        >
                          Save
                        </button>
                        <button className="cancel-buttonPlaytrees" onClick={handleCancelEdit}>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h4>{selectedPlaytree.name}</h4>
                      <div className="edit-buttonsPlaytrees">
                        <button
                          className="edit-buttonPlaytrees"
                          onClick={() => {
                            setIsEditing(true);
                            setEditName(selectedPlaytree.name);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-buttonPlaytrees"
                          onClick={handleDeletePlaytree}
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="playtree-subheader">
                <h5>Number of playlists: {sublists.length} playlists</h5>
                {isEditing && sublists.length > 0 && (
                  <button className="reset-button" onClick={handleResetToAlphabetical}>
                    Reset A→Z
                  </button>
                )}
              </div>
              <table className="playlist-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Songs</th>
                    {isEditing && <th>Order</th>}
                    {isEditing && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {sublists.map((playlist, index) => (
                    <tr
                      key={playlist.id}
                      style={{
                        backgroundColor: playlistsMarkedForRemoval.includes(playlist.id)
                          ? '#ffcccc'
                          : 'transparent',
                      }}
                    >
                      <td>
                        <span
                          onClick={() => handlePlaylistClick(playlist.id)}
                          className="playlist-link"
                          style={{ cursor: 'pointer' }}
                        >
                          {getPlaylistDisplayName(playlist)}
                        </span>
                      </td>
                      <td>{playlist.playlistType}</td>
                      <td>{playlist.tracks.length}</td>
                      {isEditing && (
                        <td className="order-buttons">
                          <button
                            className="order-button"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                          >
                            ▲
                          </button>
                          <button
                            className="order-button"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === sublists.length - 1}
                          >
                            ▼
                          </button>
                        </td>
                      )}
                      {isEditing && (
                        <td>
                          <button
                            className={
                              playlistsMarkedForRemoval.includes(playlist.id)
                                ? 'add-button'
                                : 'remove-button'
                            }
                            onClick={() => handleToggleRemoval(playlist.id)}
                          >
                            {playlistsMarkedForRemoval.includes(playlist.id) ? '+' : '-'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>Select a playtree to view details.</p>
          )}
        </div>

        <div className="column pm-column pm-column--work">
          {selectedPlaytree ? (
            <>
              {/* Intermediate Table */}
              {selectedPlaylists.length > 0 && (
                <div className="intermediate-table">
                  <h5>Selected Playlists</h5>
                  <table className="playlist-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPlaylists.map((playlist) => (
                        <tr key={playlist.id}>
                          <td>
                            <span
                              onClick={() => handlePlaylistClick(playlist.id)}
                              className="playlist-link"
                              style={{ cursor: 'pointer' }}
                            >
                              {getPlaylistDisplayName(playlist)}
                            </span>
                          </td>
                          <td>{playlist.playlistType}</td>
                          <td>
                            <button
                              className="remove-button"
                              onClick={() => handleRemoveFromIntermediate(playlist.id)}
                            >
                              X
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="intermediate-actions">
                    <button className="edit-button" onClick={handleAddToPlaytree}>
                      Add to Current Playtree
                    </button>
                    <button
                      className="cancel-button"
                      onClick={() => setSelectedPlaylists([])}
                    >
                      Delete All
                    </button>
                  </div>
                </div>
              )}

              <input
                type="text"
                placeholder="Search playlists..."
                className="search-box"
                value={playlistSearchQuery}
                onChange={(e) => setPlaylistSearchQuery(e.target.value)}
              />
              <table className="playlist-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>
                      <button
                        className="add-button"
                        onClick={handleAddAllToIntermediate}
                      >
                        Add All
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlaylists.map((playlist) => (
                    <tr key={playlist.id}>
                      <td>
                        <span
                          onClick={() => handlePlaylistClick(playlist.id)}
                          className="playlist-link"
                          style={{ cursor: 'pointer' }}
                        >
                          {getPlaylistDisplayName(playlist)}
                        </span>
                      </td>
                      <td>{playlist.playlistType}</td>
                      <td>
                        <button
                          className="add-button"
                          onClick={() => handleAddToIntermediate(playlist)}
                        >
                          +
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p>Select a playtree to add playlists.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaytreeManager;
